import { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  IconButton,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import {
  Area,
  Brush,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import PvExportMenu from "./PvExportMenu";
import {
  BAND_FILL_COLOR,
  HISTORICAL_COLOR,
  SCADA_COLOR,
  aggregateRows,
  buildCsv,
  downloadBlob,
  exportChartPng,
  forecastColor,
  forecastSeriesKey,
  formatMwh,
  formatPct,
  formatRunTimestamp,
  formatTooltipDate,
  formatTooltipValue,
  mergeSeriesByTime,
  withDeviationBand,
} from "./pvGenerationUtils";

function formatAxisTick(value, granularity, timeZone) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const opts = timeZone ? { timeZone } : {};
  if (granularity === "daily") {
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", ...opts });
  }
  const datePart = date.toLocaleDateString("en-GB", { day: "numeric", month: "short", ...opts });
  const timePart = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, ...opts });
  return `${datePart}, ${timePart}`;
}

function ChartTooltip({ active, payload, label, forecastKeys, timeZone }) {
  if (!active || !payload?.length) return null;
  const visible = payload.filter((p) => ["historical", "measured", ...forecastKeys].includes(p.dataKey));
  if (!visible.length) return null;
  return (
    <Paper elevation={4} sx={{ p: 1.25, borderRadius: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
        {formatTooltipDate(label, timeZone)}
      </Typography>
      <Stack spacing={0.25}>
        {visible.map((entry) => (
          <Stack key={entry.dataKey} direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: entry.color, flexShrink: 0 }} />
            <Typography variant="caption">
              {entry.name}: {formatTooltipValue(entry.value)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

function LegendSwatch({ color, dashed, label }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box sx={{ width: 22, height: 0, borderTop: `3px ${dashed ? "dashed" : "solid"} ${color}` }} />
      <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
        {label}
      </Typography>
    </Stack>
  );
}

export default function PvGenerationChart({
  variant = "inline",
  historicalRun,
  forecastEntries = [],
  historicalSeries,
  measuredSeries,
  measuredAvailable,
  historicalMwh,
  forecastRows = [],
  timeZone,
  onFullscreen,
  onExportComparisonExcel,
  onExportRun,
  exporting,
  exportError,
  exportBaseName = "pv-generation",
  comparisonExportLabel = "Current comparison to Excel",
}) {
  const [granularity, setGranularity] = useState("15min");
  const [zoomEnabled, setZoomEnabled] = useState(false);
  const containerRef = useRef(null);

  const forecastSeriesList = useMemo(
    () => forecastEntries.map(({ run, series }) => ({ runId: run.run_id, series })),
    [forecastEntries]
  );
  const forecastKeys = useMemo(
    () => forecastEntries.map(({ run }) => forecastSeriesKey(run.run_id)),
    [forecastEntries]
  );

  const rawRows = useMemo(
    () => mergeSeriesByTime({ historicalSeries, forecastSeriesList, measuredSeries }),
    [historicalSeries, forecastSeriesList, measuredSeries]
  );
  const aggregatedRows = useMemo(() => aggregateRows(rawRows, granularity), [rawRows, granularity]);
  const displayRows = useMemo(() => {
    if (forecastEntries.length !== 1) return aggregatedRows;
    return withDeviationBand(aggregatedRows, "historical", forecastKeys[0]);
  }, [aggregatedRows, forecastEntries.length, forecastKeys]);

  const hasData = displayRows.length > 0;

  const handleExportPng = () => {
    const svgEl = containerRef.current?.querySelector("svg");
    exportChartPng(svgEl, `${exportBaseName}-chart-${Date.now()}.png`).catch(() => {});
  };

  const handleExportCsv = () => {
    const columns = [
      { label: "Time", value: (r) => new Date(r.time).toISOString() },
      { label: "Historical (kW)", value: (r) => (r.historical != null ? r.historical.toFixed(2) : "") },
      ...forecastEntries.map(({ run }) => ({
        label: `Forecast ${formatRunTimestamp(run.created_at, timeZone)} (kW)`,
        value: (r) => {
          const v = r[forecastSeriesKey(run.run_id)];
          return v != null ? v.toFixed(2) : "";
        },
      })),
      { label: "Measured (kW)", value: (r) => (r.measured != null ? r.measured.toFixed(2) : "") },
    ];
    const csv = buildCsv(rawRows, columns);
    downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), `${exportBaseName}-raw-15min.csv`);
  };

  const exportItems = [
    ...(onExportComparisonExcel
      ? [
          {
            label: comparisonExportLabel,
            onClick: onExportComparisonExcel,
            disabled: !historicalRun && forecastEntries.length === 0,
          },
        ]
      : []),
    {
      label: "Historical run",
      onClick: () => onExportRun(historicalRun),
      disabled: !historicalRun,
    },
    ...forecastEntries.map(({ run }) => ({
      label: `Forecast run · ${formatRunTimestamp(run.created_at, timeZone)}`,
      onClick: () => onExportRun(run),
      disabled: false,
    })),
    { label: "Chart as PNG", onClick: handleExportPng, disabled: !hasData },
    { label: "Raw 15-minute values as CSV", onClick: handleExportCsv, disabled: !hasData },
  ];

  const subtitle = useMemo(() => {
    if (forecastRows.length === 1) {
      const row = forecastRows[0];
      return [
        historicalMwh != null ? `Historical weather — ${formatMwh(historicalMwh)}` : null,
        row.forecastMwh != null ? `Forecast weather — ${formatMwh(row.forecastMwh)}` : null,
        row.errorPct != null ? `Difference ${formatPct(row.errorPct)}` : null,
      ]
        .filter(Boolean)
        .join(" · ");
    }
    return [
      historicalMwh != null ? `Historical weather — ${formatMwh(historicalMwh)}` : null,
      forecastRows.length > 0 ? `Comparing ${forecastRows.length} forecast runs` : null,
    ]
      .filter(Boolean)
      .join(" · ");
  }, [historicalMwh, forecastRows]);

  return (
    <Stack spacing={1.5} sx={variant === "fullscreen" ? { height: "100%" } : undefined}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1.5}
        sx={variant === "fullscreen" ? { flex: "0 0 auto" } : undefined}
      >
        <Stack spacing={0.25}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Generation profile
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {subtitle}
            </Typography>
          )}
          <Stack direction="row" spacing={2} sx={{ mt: 0.5, flexWrap: "wrap", rowGap: 0.5 }}>
            <LegendSwatch color={HISTORICAL_COLOR} dashed={false} label="Historical weather" />
            {forecastEntries.map(({ run }, idx) => (
              <LegendSwatch
                key={run.run_id}
                color={forecastColor(idx)}
                dashed
                label={`Forecast · ${formatRunTimestamp(run.created_at, timeZone)}`}
              />
            ))}
            {measuredAvailable && <LegendSwatch color={SCADA_COLOR} dashed={false} label="SCADA" />}
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ rowGap: 1 }}>
          <ToggleButtonGroup
            size="small"
            value={granularity}
            exclusive
            onChange={(_, value) => value && setGranularity(value)}
          >
            <ToggleButton value="15min" sx={{ textTransform: "none", fontWeight: 700, px: 1.25 }}>
              15 min
            </ToggleButton>
            <ToggleButton value="hourly" sx={{ textTransform: "none", fontWeight: 700, px: 1.25 }}>
              Hourly
            </ToggleButton>
            <ToggleButton value="daily" sx={{ textTransform: "none", fontWeight: 700, px: 1.25 }}>
              Daily
            </ToggleButton>
          </ToggleButtonGroup>
          <IconButton
            size="small"
            color={zoomEnabled ? "primary" : "default"}
            onClick={() => setZoomEnabled((v) => !v)}
            title="Zoom"
          >
            <ZoomInIcon fontSize="small" />
          </IconButton>
          <PvExportMenu items={exportItems} busy={exporting} error={exportError} />
          {variant === "inline" && onFullscreen && (
            <IconButton size="small" onClick={onFullscreen} title="Full screen">
              <FullscreenIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Stack>

      {hasData ? (
        <Box
          ref={containerRef}
          sx={
            variant === "fullscreen"
              ? { flex: "1 1 auto", minHeight: 0 }
              : { height: 420 }
          }
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={displayRows} margin={{ left: 24, right: 40, bottom: 24, top: 12 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="time"
                type="number"
                domain={["dataMin", "dataMax"]}
                tick={{ fontSize: 12, fontWeight: 700 }}
                tickFormatter={(v) => formatAxisTick(v, granularity, timeZone)}
              />
              <YAxis
                tick={{ fontSize: 12, fontWeight: 700 }}
                width={90}
                label={{ value: "kW", angle: -90, position: "insideLeft", offset: 14 }}
              />
              <RechartsTooltip content={<ChartTooltip forecastKeys={forecastKeys} timeZone={timeZone} />} />
              {forecastEntries.length === 1 && (
                <>
                  <Area
                    dataKey="bandBase"
                    stackId="band"
                    stroke="none"
                    fill="transparent"
                    isAnimationActive={false}
                    legendType="none"
                  />
                  <Area
                    dataKey="bandHeight"
                    stackId="band"
                    stroke="none"
                    fill={BAND_FILL_COLOR}
                    fillOpacity={0.12}
                    isAnimationActive={false}
                    legendType="none"
                  />
                </>
              )}
              <Line
                type="monotone"
                dataKey="historical"
                name="Historical weather"
                stroke={HISTORICAL_COLOR}
                strokeWidth={2.5}
                dot={false}
                connectNulls={false}
              />
              {forecastEntries.map(({ run }, idx) => (
                <Line
                  key={run.run_id}
                  type="monotone"
                  dataKey={forecastSeriesKey(run.run_id)}
                  name={`Forecast · ${formatRunTimestamp(run.created_at, timeZone)}`}
                  stroke={forecastColor(idx)}
                  strokeWidth={2.5}
                  strokeDasharray="6 4"
                  dot={false}
                  connectNulls={false}
                />
              ))}
              {measuredAvailable && (
                <Line
                  type="monotone"
                  dataKey="measured"
                  name="Measured (SCADA)"
                  stroke={SCADA_COLOR}
                  strokeWidth={3}
                  dot={false}
                  connectNulls={false}
                />
              )}
              {zoomEnabled && (
                <Brush
                  dataKey="time"
                  height={22}
                  travellerWidth={10}
                  tickFormatter={(v) => formatAxisTick(v, granularity, timeZone)}
                  stroke="#90a4ae"
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      ) : (
        <Alert severity="info">
          Select a historical-weather run and/or one or more forecast-weather runs from the run
          history below to plot them here.
        </Alert>
      )}
    </Stack>
  );
}
